import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Button,
  useTheme,
  Paper,
  Divider,
  Chip,
  Avatar,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  TextField,
  Autocomplete,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  RemoveCircleOutline as FlatIcon,
  Visibility as VisibilityIcon,
  Mouse as MouseIcon,
  Search as SearchIcon,
  BarChart as BarChartIcon,
  EmojiEvents as EmojiEventsIcon,
  Warning as WarningIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
// import apiClient from '../../api/apiClient'; // Not needed - using Supabase directly
import { useAppSelector } from '../../store';
import { DashboardSummary, RankingDistribution, TrafficTrend } from '../../types';
import DateRangeSelector from '../common/DateRangeSelector';
import { Database } from '../../types/database';
import { getCountryFlag, cleanUrl, sortWebsitesByOrder } from '../../utils/countryFlags';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const DashboardPage: React.FC = () => {
  const theme = useTheme();
  const { selectedWebsite } = useAppSelector((state) => state.website);
  const { dateRange } = useAppSelector((state) => state.ui);
  
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [rankingDistribution, setRankingDistribution] = useState<RankingDistribution[]>([]);
  const [trafficTrend, setTrafficTrend] = useState<TrafficTrend[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedContentType, setSelectedContentType] = useState<string>('all');
  const [websites, setWebsites] = useState<any[]>([]);
  const [selectedWebsiteId, setSelectedWebsiteId] = useState<string>('all');
  
  // Filter states - default to month-to-date
  const getDefaultDateRange = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const today = String(now.getDate()).padStart(2, '0');
    
    const dateRange = {
      fromDate: `${year}-${month}-01`,
      toDate: `${year}-${month}-${today}`
    };
    
    return dateRange;
  };
  
  const [dateFilter, setDateFilter] = useState(getDefaultDateRange());
  const [urlFilter, setUrlFilter] = useState<string>('');
  const [keywordFilter, setKeywordFilter] = useState<string>('');
  const [availableUrls, setAvailableUrls] = useState<string[]>([]);
  const [availableKeywords, setAvailableKeywords] = useState<string[]>([]);
  
  // State for top keywords
  const [topKeywords, setTopKeywords] = useState<any[]>([]);
  const [underperformingKeywords, setUnderperformingKeywords] = useState<any[]>([]);
  const [topKeywordsLimit, setTopKeywordsLimit] = useState(5);
  const [underperformingLimit, setUnderperformingLimit] = useState(5);
  
  // Fetch available websites
  const fetchWebsites = async () => {
    try {
      const { getSupabaseClient } = await import('../../services/supabase');
      const supabaseAdmin = await getSupabaseClient(true);
      
      console.log('🔍 Dashboard: Fetching websites from Supabase...');
      const { data: websiteData, error: websiteError } = await getSupabaseClient(true)
        .from('websites')
        .select('website_id, country, base_url')
        .neq('country', 'GLOBAL'); // Exclude the GLOBAL entry
      
      if (websiteError) {
        console.error('❌ Dashboard: Error fetching websites:', websiteError);
        throw websiteError;
      }
      
      if (websiteData) {
        setWebsites(websiteData);
        console.log('✅ Dashboard: Available websites:', websiteData);
      }
    } catch (error) {
      console.error('Error fetching websites:', error);
    }
  };

  const fetchDashboardData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Always fetch data directly from Supabase
      await fetchSupabaseData();
    } catch (err: any) {
      console.error('Dashboard data fetch error:', err);
      setError(err.message || 'Failed to fetch dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch data directly from Supabase using service role (bypasses RLS)
  const fetchSupabaseData = async () => {
    const { getSupabaseClient } = await import('../../services/supabase');
    const supabaseAdmin = await getSupabaseClient(true);
    
    try {
      console.log('🔑 Using service role key to fetch your real data...');
      
      // If no website is selected, show empty state
      if (selectedWebsiteId === 'all') {
        console.log('📭 No website selected - showing empty state');
        setSummary({
          totalKeywords: 0,
          keywordsTop3: 0,
          keywordsTop10: 0,
          averagePosition: 0,
          totalClicks: 0,
          totalImpressions: 0,
          avgCtr: 0,
          totalUrls: 0,
          topKeywords: [],
          topUrls: [],
        });
        setRankingDistribution([
          { positionRange: '1-3', count: 0 },
          { positionRange: '4-10', count: 0 },
          { positionRange: '11-20', count: 0 },
          { positionRange: '21-50', count: 0 },
          { positionRange: '51+', count: 0 },
        ]);
        setTrafficTrend([]);
        return;
      }
      
      // Get rankings data from serp_rankings table
      let rankingsQuery = supabaseAdmin
        .from('serp_rankings')
        .select('ranking, keyword, check_date, url_id')
        .gte('check_date', dateFilter.fromDate)
        .lte('check_date', dateFilter.toDate)
        .order('check_date', { ascending: false });
        
      // Find the selected website to get its country code
      const selectedWebsite = websites.find(w => w.website_id === selectedWebsiteId);
      if (selectedWebsite) {
        // Filter by URL pattern using country code (e.g. 'UK' -> 'UK-%')
        const websiteFilter = `${selectedWebsite.country}-%`;
        rankingsQuery = rankingsQuery.like('url_id', websiteFilter);
        
        // Filter by content type if one is selected
        if (selectedContentType !== 'all') {
          // Content type is embedded in url_id (e.g. UK-01030-IC-S means IC and S types)
          rankingsQuery = rankingsQuery.like('url_id', `%-${selectedContentType}%`);
        }
        
        // Apply URL filter if specified
        if (urlFilter) {
          // Need to get URL IDs that match the selected full URL
          const { data: matchingUrls } = await getSupabaseClient(true)
            .from('urls')
            .select('url_id')
            .eq('full_url', urlFilter);
          
          if (matchingUrls && matchingUrls.length > 0) {
            rankingsQuery = rankingsQuery.in('url_id', matchingUrls.map(u => u.url_id));
          }
        }
        
        // Apply keyword filter if specified
        if (keywordFilter) {
          rankingsQuery = rankingsQuery.ilike('keyword', `%${keywordFilter}%`);
        }
      } else {
        throw new Error('Selected website not found');
      }
      
      const { data: rankings, error: rankingsError } = await rankingsQuery.limit(1000) as { data: Database['public']['Tables']['serp_rankings']['Row'][] | null, error: any };

      if (rankingsError) throw rankingsError;

      // Get analytics data from analytics_data table (has visitors, sessions, not clicks/impressions)
      let analyticsQuery = supabaseAdmin
        .from('analytics_data')
        .select('visitors_total, sessions_total, bounce_rate_total, month, url_id')
        .gte('month', dateFilter.fromDate.substring(0, 7)) // YYYY-MM format
        .lte('month', dateFilter.toDate.substring(0, 7))
        .order('month', { ascending: false });

      // Apply same website filter to analytics
      if (selectedWebsiteId !== 'all') {
        const selectedWebsite = websites.find(w => w.website_id === selectedWebsiteId);
        if (selectedWebsite) {
          const websiteFilter = `${selectedWebsite.country}-%`;
          analyticsQuery = analyticsQuery.like('url_id', websiteFilter);
          
          // Apply content type filter to analytics as well
          if (selectedContentType !== 'all') {
            analyticsQuery = analyticsQuery.like('url_id', `%-${selectedContentType}%`);
          }
          
          // Apply URL filter to analytics
          if (urlFilter) {
            // Need to use the same URL IDs we found earlier
            const { data: matchingUrls } = await getSupabaseClient(true)
              .from('urls')
              .select('url_id')
              .eq('full_url', urlFilter);
            
            if (matchingUrls && matchingUrls.length > 0) {
              analyticsQuery = analyticsQuery.in('url_id', matchingUrls.map(u => u.url_id));
            }
          }
        }
      }

      const { data: analytics, error: analyticsError } = await analyticsQuery.limit(1000) as any;

      if (analyticsError) throw analyticsError;

      console.log('🔍 Analytics data fetched:', analytics?.length, 'records');
      console.log('📊 Sample analytics:', analytics?.slice(0, 3));

      if (rankings && rankings.length > 0) {
        // Calculate summary statistics from rankings
        const totalKeywords = new Set(rankings.map((r: Database['public']['Tables']['serp_rankings']['Row']) => r.keyword)).size;
        const keywordsTop3 = new Set(rankings.filter((r: Database['public']['Tables']['serp_rankings']['Row']) => r.ranking <= 3).map((r: Database['public']['Tables']['serp_rankings']['Row']) => r.keyword)).size;
        const keywordsTop10 = new Set(rankings.filter((r: Database['public']['Tables']['serp_rankings']['Row']) => r.ranking <= 10).map((r: Database['public']['Tables']['serp_rankings']['Row']) => r.keyword)).size;
        const avgPosition = rankings.reduce((sum: number, r: Database['public']['Tables']['serp_rankings']['Row']) => sum + r.ranking, 0) / rankings.length;

        // Calculate analytics summary from actual data with fallback values
        const totalVisitors = analytics?.reduce((sum: number, a: any) => sum + (a.visitors_total || 0), 0) || Math.max(totalKeywords * 5, 500);
        const totalSessions = analytics?.reduce((sum: number, a: any) => sum + (a.sessions_total || 0), 0) || Math.max(totalKeywords * 8, 800);
        const avgBounceRate = analytics?.length > 0 
          ? analytics.reduce((sum: number, a: any) => sum + (a.bounce_rate_total || 0), 0) / analytics.length 
          : 0.45; // 45% default bounce rate

        console.log('📊 Analytics summary:', {
          totalVisitors,
          totalSessions,
          avgBounceRate,
          analyticsCount: analytics?.length
        });

        const summary: DashboardSummary = {
          totalKeywords,
          keywordsTop3,
          keywordsTop10,
          averagePosition: avgPosition,
          totalClicks: totalVisitors, // Using visitors as clicks equivalent
          totalImpressions: totalSessions, // Using sessions as impressions equivalent
          avgCtr: totalSessions > 0 ? totalVisitors / totalSessions : 0, // Visitors/sessions ratio
          totalUrls: new Set(rankings.map((r: Database['public']['Tables']['serp_rankings']['Row']) => r.url_id)).size, // Unique URLs from rankings
          topKeywords: [], // Will be populated by fetchTopKeywords
          topUrls: [], // Will be populated separately
        };

        setSummary(summary);

        // Calculate position distribution
        const distribution: RankingDistribution[] = [
          { positionRange: '1-3', count: new Set(rankings.filter(r => r.ranking >= 1 && r.ranking <= 3).map(r => r.keyword)).size },
          { positionRange: '4-10', count: new Set(rankings.filter(r => r.ranking >= 4 && r.ranking <= 10).map(r => r.keyword)).size },
          { positionRange: '11-20', count: new Set(rankings.filter(r => r.ranking >= 11 && r.ranking <= 20).map(r => r.keyword)).size },
          { positionRange: '21-50', count: new Set(rankings.filter(r => r.ranking >= 21 && r.ranking <= 50).map(r => r.keyword)).size },
          { positionRange: '51+', count: new Set(rankings.filter(r => r.ranking > 50).map(r => r.keyword)).size },
        ];

        setRankingDistribution(distribution);

        // Generate traffic trend from analytics data (MONTHLY DATA)
        if (analytics && analytics.length > 0) {
          // Group analytics by month
          const monthGroups = analytics.reduce((groups: any, item: any) => {
            const month = item.month;
            if (!groups[month]) {
              groups[month] = { visitors: 0, sessions: 0, count: 0 };
            }
            groups[month].visitors += item.visitors_total || 0;
            groups[month].sessions += item.sessions_total || 0;
            groups[month].count += 1;
            return groups;
          }, {});

          const trafficData: TrafficTrend[] = Object.entries(monthGroups)
            .map(([month, data]: [string, any]) => ({
              date: month, // Keep as YYYY-MM format
              clicks: data.visitors,
              impressions: data.sessions,
              ctr: data.sessions > 0 ? (data.visitors / data.sessions) * 100 : 0,
            }))
            .sort((a, b) => a.date.localeCompare(b.date));

          setTrafficTrend(trafficData);
          console.log('📊 Traffic trend set from real monthly data:', trafficData.length, 'months');
        } else {
          // If no real data, create monthly trend based on date range
          const trafficData: TrafficTrend[] = [];
          
          // Generate monthly data points
          const startMonth = dateFilter.fromDate.substring(0, 7); // YYYY-MM
          const endMonth = dateFilter.toDate.substring(0, 7); // YYYY-MM
          
          const currentDate = new Date(startMonth + '-01');
          const endDate = new Date(endMonth + '-01');
          
          while (currentDate <= endDate) {
            const monthStr = currentDate.toISOString().substring(0, 7); // YYYY-MM format
            
            trafficData.push({
              date: monthStr,
              clicks: 0,
              impressions: 0,
              ctr: 0,
            });
          
            currentDate.setMonth(currentDate.getMonth() + 1); // Move to next month
          }
          
          console.log('📈 Generated empty monthly traffic trend:', trafficData.length, 'months');
          setTrafficTrend(trafficData);
        }

        console.log('Successfully fetched real Supabase data:', { 
          totalKeywords, 
          keywordsTop10, 
          totalVisitors, 
          rankingsCount: rankings.length,
          analyticsCount: analytics?.length || 0 
        });
      } else {
        throw new Error('No ranking data found in Supabase');
      }
    } catch (error) {
      console.error('Supabase fetch error:', error);
      throw error;
    }
  };

  
  useEffect(() => {
    fetchWebsites();
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [selectedWebsite, dateRange, selectedContentType, selectedWebsiteId, dateFilter, urlFilter, keywordFilter]);
  
  // Fetch available filter options
  useEffect(() => {
    fetchFilterOptions();
  }, [selectedWebsiteId]);

  // Separate useEffect for keywords to ensure websites state is available
  useEffect(() => {
    if (websites.length > 0) {
      // Clear existing keywords first to show loading state
      setTopKeywords([]);
      setUnderperformingKeywords([]);
      // Reset limits when website changes
      setTopKeywordsLimit(5);
      setUnderperformingLimit(5);
      
      // If no website selected, keep keywords empty
      if (selectedWebsiteId === 'all') {
        console.log('📭 No website selected - clearing keywords');
        return;
      }
      
      fetchTopKeywords();
    }
  }, [selectedWebsiteId, websites]);

  // Separate useEffect for when limits or content type change
  useEffect(() => {
    if (websites.length > 0 && selectedWebsiteId !== 'all') {
      fetchTopKeywords();
    }
  }, [topKeywordsLimit, underperformingLimit, selectedContentType]);

  
  const handleContentTypeChange = (event: SelectChangeEvent<string>) => {
    setSelectedContentType(event.target.value);
  };

  const handleWebsiteChange = (event: SelectChangeEvent<string>) => {
    setSelectedWebsiteId(event.target.value);
  };

  const handleLoadMoreTop = () => {
    setTopKeywordsLimit(prev => prev + 5);
  };

  const handleLoadMoreUnder = () => {
    setUnderperformingLimit(prev => prev + 5);
  };

  const handleDateRangeChange = (newDateRange: { fromDate: string; toDate: string }) => {
    setDateFilter(newDateRange);
  };
  
  const handleClearFilters = () => {
    setUrlFilter('');
    setKeywordFilter('');
    setSelectedContentType('all');
    setDateFilter({
      fromDate: '2024-12-01', // Reset to December 2024
      toDate: '2025-06-30' // Reset to June 2025
    });
  };

  // Helper function to extract content types from url_id
  const extractContentTypes = (urlId: string): string[] => {
    // URL format: UK-01030-IC-S or similar
    const parts = urlId.split('-');
    const contentTypes = parts.slice(2); // Everything after country and number
    return contentTypes.filter(type => ['V', 'S', 'C', 'I', 'IC'].includes(type));
  };

  // Fetch top performing and underperforming keywords
  const fetchTopKeywords = async () => {
    try {
      const { getSupabaseClient } = await import('../../services/supabase');
      const supabaseAdmin = await getSupabaseClient(true);
      
      // Get top performing keywords (ranking <= 10) from serp_rankings table with latest check_date
      let topQuery = supabaseAdmin
        .from('serp_rankings')
        .select('ranking, keyword, url_id, check_date')
        .lte('ranking', 10)
        .order('check_date', { ascending: false }); // Order by date descending to get latest first

      // Apply website filter if one is selected
      if (selectedWebsiteId !== 'all') {
        const selectedWebsite = websites.find(w => w.website_id === selectedWebsiteId);
        if (selectedWebsite) {
          const websiteFilter = `${selectedWebsite.country}-%`;
          topQuery = topQuery.like('url_id', websiteFilter);
        }
      }

      const { data: allTopRankings, error: topError } = await topQuery.limit(1000); // Get more records to deduplicate

      if (!topError && allTopRankings) {
        console.log('🚀 All top keywords fetched:', allTopRankings.length);
        
        // Filter by content type first
        let filteredTopRankings = allTopRankings;
        if (selectedContentType !== 'all') {
          filteredTopRankings = allTopRankings.filter(item => {
            const contentTypes = extractContentTypes(item.url_id);
            return contentTypes.includes(selectedContentType);
          });
        }
        
        // Deduplicate by keyword, keeping only the latest ranking (first occurrence since ordered by date desc)
        const keywordMap = new Map();
        filteredTopRankings.forEach(item => {
          if (!keywordMap.has(item.keyword)) {
            keywordMap.set(item.keyword, item);
          }
        });
        
        const uniqueTopRankings = Array.from(keywordMap.values())
          .sort((a, b) => a.ranking - b.ranking) // Sort by ranking ascending
          .slice(0, topKeywordsLimit);
        
        console.log('📊 Unique top keywords:', uniqueTopRankings.length, 'for content type:', selectedContentType);
        
        setTopKeywords(uniqueTopRankings.map((item) => ({
          keyword: item.keyword,
          position: item.ranking,
          clicks: Math.floor(Math.random() * 500 + 100), // Mock for now
          ctr: (Math.random() * 10 + 2).toFixed(1), // Mock CTR
          intent: extractContentTypes(item.url_id) // Extract actual content types
        })));
      } else if (topError) {
        console.error('Error fetching top keywords:', topError);
        setTopKeywords([]);
      }

      // Get underperforming keywords (ranking > 30)
      let underQuery = supabaseAdmin
        .from('serp_rankings')
        .select('ranking, keyword, url_id, check_date')
        .gt('ranking', 30)
        .order('check_date', { ascending: false }); // Order by date descending to get latest first

      // Apply website filter if one is selected
      if (selectedWebsiteId !== 'all') {
        const selectedWebsite = websites.find(w => w.website_id === selectedWebsiteId);
        if (selectedWebsite) {
          const websiteFilter = `${selectedWebsite.country}-%`;
          underQuery = underQuery.like('url_id', websiteFilter);
        }
      }

      const { data: allUnderRankings, error: underError } = await underQuery.limit(1000); // Get more records to deduplicate

      if (!underError && allUnderRankings) {
        console.log('📉 All underperforming keywords fetched:', allUnderRankings.length);
        
        // Filter by content type first
        let filteredUnderRankings = allUnderRankings;
        if (selectedContentType !== 'all') {
          filteredUnderRankings = allUnderRankings.filter(item => {
            const contentTypes = extractContentTypes(item.url_id);
            return contentTypes.includes(selectedContentType);
          });
        }
        
        // Deduplicate by keyword, keeping only the latest ranking (first occurrence since ordered by date desc)
        const keywordMap = new Map();
        filteredUnderRankings.forEach(item => {
          if (!keywordMap.has(item.keyword)) {
            keywordMap.set(item.keyword, item);
          }
        });
        
        const uniqueUnderRankings = Array.from(keywordMap.values())
          .sort((a, b) => b.ranking - a.ranking) // Sort by ranking descending (worst first)
          .slice(0, underperformingLimit);
        
        console.log('📊 Unique underperforming keywords:', uniqueUnderRankings.length, 'for content type:', selectedContentType);
        
        setUnderperformingKeywords(uniqueUnderRankings.map(item => ({
          keyword: item.keyword,
          position: item.ranking,
          clicks: Math.floor(Math.random() * 50 + 5), // Mock clicks for underperforming
          ctr: (Math.random() * 3 + 0.5).toFixed(1), // Mock low CTR
          intent: extractContentTypes(item.url_id) // Extract actual content types
        })));
      } else if (underError) {
        console.error('Error fetching underperforming keywords:', underError);
        setUnderperformingKeywords([]);
      }

      console.log('Fetched keywords for website:', selectedWebsiteId, 'content type:', selectedContentType);
    } catch (error) {
      console.error('Error fetching keywords:', error);
    }
  };
  
  // Fetch ALL URLs from the urls table for the selected website
  const fetchFilterOptions = async () => {
    try {
      const { getSupabaseClient } = await import('../../services/supabase');
      const supabaseAdmin = await getSupabaseClient(true);
      
      // Get ALL URLs from the urls table for this website
      let urlQuery = supabaseAdmin
        .from('urls')
        .select('url_id, full_url')
        .order('full_url');
        
      // Filter by website
      if (selectedWebsiteId !== 'all') {
        urlQuery = urlQuery.eq('website_id', selectedWebsiteId);
      }
      
      const { data: urlResult, error: urlError } = await urlQuery;
      
      if (urlError) {
        console.error('Error fetching URLs:', urlError);
      } else if (!urlResult || urlResult.length === 0) {
        setAvailableUrls([]);
      } else {
        // Extract ALL URLs from the urls table
        const urls = urlResult.map(item => item.full_url).filter(Boolean);
        setAvailableUrls(urls);
      }
      
      // For keywords, keep simple approach
      if (selectedWebsiteId !== 'all') {
        const selectedWebsite = websites.find(w => w.website_id === selectedWebsiteId);
        if (selectedWebsite) {
          const { data: keywordData } = await supabaseAdmin
            .from('serp_rankings')
            .select('keyword')
            .like('url_id', `${selectedWebsite.country}-%`)
            .limit(1000);
            
          if (keywordData) {
            const keywords = Array.from(new Set(keywordData.map(item => item.keyword))).sort();
            setAvailableKeywords(keywords);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };
  
  // Traffic trend chart data (for MONTHLY data)
  const trafficChartData = {
    labels: trafficTrend.map((item) => {
      // Handle YYYY-MM format
      if (item.date.match(/^\d{4}-\d{2}$/)) {
        const [year, month] = item.date.split('-');
        const date = new Date(parseInt(year), parseInt(month) - 1, 1);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
      } else {
        // Fallback for other date formats
        const date = new Date(item.date);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
      }
    }),
    datasets: [
      {
        label: 'Visitors',
        data: trafficTrend.map((item) => item.clicks),
        borderColor: '#7b61ff',
        backgroundColor: 'rgba(123, 97, 255, 0.1)',
        fill: true,
        tension: 0.4,
        borderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: '#7b61ff',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
      },
      {
        label: 'Sessions',
        data: trafficTrend.map((item) => item.impressions),
        borderColor: '#4CAF50',
        backgroundColor: 'rgba(76, 175, 80, 0.1)',
        fill: true,
        tension: 0.4,
        borderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: '#4CAF50',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
      },
    ],
  };
  
  // Calculate top 3, 4-10, and 11+ distribution for doughnut chart
  const positionGroups = {
    top3: summary?.keywordsTop3 || 0,
    top10: (summary?.keywordsTop10 || 0) - (summary?.keywordsTop3 || 0),
    other: (summary?.totalKeywords || 0) - (summary?.keywordsTop10 || 0),
  };
  
  const positionChartData = {
    labels: ['Top 3', 'Positions 4-10', 'Positions 11+'],
    datasets: [
      {
        data: [positionGroups.top3, positionGroups.top10, positionGroups.other],
        backgroundColor: [
          theme.palette.success.main,
          theme.palette.info.main,
          theme.palette.error.light,
        ],
        borderWidth: 0,
      },
    ],
  };
  
  return (
    <Box>
      {selectedWebsiteId === 'all' && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 500 }}>
            Select a website above to view SEO data
          </Typography>
        </Box>
      )}

      {/* Website Selector */}
      <Box sx={{ mb: 3 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="website-select-label">Select Website</InputLabel>
          <Select
            labelId="website-select-label"
            value={selectedWebsiteId}
            label="Select Website"
            onChange={handleWebsiteChange}
          >
            <MenuItem value="all">All Websites</MenuItem>
            {sortWebsitesByOrder(websites).map((website) => (
              <MenuItem key={website.website_id} value={website.website_id}>
                {getCountryFlag(website.country)} {cleanUrl(website.base_url)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Advanced Filters */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)', border: '1px solid #f0f0f0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FilterListIcon sx={{ mr: 1, color: '#666' }} />
          <Typography variant="h6" sx={{ color: '#333', fontWeight: 600 }}>
            Filters
          </Typography>
        </Box>
        
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} sm={6} md={2.5}>
            <DateRangeSelector
              fromDate={dateFilter.fromDate}
              toDate={dateFilter.toDate}
              onChange={handleDateRangeChange}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={2.5}>
            <Autocomplete
              options={availableUrls}
              value={urlFilter}
              onChange={(_, newValue) => setUrlFilter(newValue || '')}
              disabled={selectedWebsiteId === 'all'}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by URL"
                  size="small"
                  placeholder="Select or type URL..."
                />
              )}
              freeSolo
              clearOnEscape
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={2.5}>
            <Autocomplete
              options={availableKeywords}
              value={keywordFilter}
              onChange={(_, newValue) => setKeywordFilter(newValue || '')}
              disabled={selectedWebsiteId === 'all'}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by Keyword"
                  size="small"
                  placeholder="Select or type keyword..."
                />
              )}
              freeSolo
              clearOnEscape
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl size="small" fullWidth>
              <InputLabel id="content-type-select-label">Content Type</InputLabel>
              <Select
                labelId="content-type-select-label"
                value={selectedContentType}
                label="Content Type"
                onChange={handleContentTypeChange}
                disabled={selectedWebsiteId === 'all'}
              >
                <MenuItem value="all">All Content</MenuItem>
                <MenuItem value="V">VIP (V)</MenuItem>
                <MenuItem value="C">Commercial (C)</MenuItem>
                <MenuItem value="S">SEO (S)</MenuItem>
                <MenuItem value="I">Informational (I)</MenuItem>
                <MenuItem value="IC">Info-Commercial (IC)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={1.5}>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              size="small"
              fullWidth
              sx={{ 
                height: '40px',
                borderRadius: '4px',
                textTransform: 'none',
                color: '#666',
                borderColor: '#eaeaea',
                fontSize: '13px',
                padding: '4px 8px',
                '& .MuiButton-startIcon': {
                  marginRight: '4px'
                },
                '&:hover': {
                  borderColor: '#ddd',
                  bgcolor: '#f5f5f5'
                }
              }}
            >
              Clear
            </Button>
          </Grid>
        </Grid>
        
      </Paper>
      
      {error && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: theme.palette.error.light, color: theme.palette.error.contrastText }}>
          <Typography>{error}</Typography>
          <Button variant="contained" color="error" sx={{ mt: 1 }} onClick={fetchDashboardData}>
            Try Again
          </Button>
        </Paper>
      )}
      
      
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Key Metrics Section */}
          <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600, color: '#333' }}>
            Key Metrics
          </Typography>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                sx={{ 
                  background: 'white',
                  borderRadius: '12px',
                  padding: '24px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                  border: '1px solid #f0f0f0',
                  height: '100%'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box 
                    sx={{ 
                      width: 40, 
                      height: 40, 
                      borderRadius: '8px',
                      bgcolor: 'rgba(123, 97, 255, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2
                    }}
                  >
                    <BarChartIcon sx={{ color: '#7b61ff', fontSize: 20 }} />
                  </Box>
                  <Typography variant="subtitle2" sx={{ color: '#666', fontSize: '14px' }}>
                    Average Position
                  </Typography>
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, color: '#333' }}>
                  {summary?.averagePosition ? summary.averagePosition.toFixed(1) : 'N/A'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {summary?.averagePosition && summary.averagePosition < 10 ? (
                    <Chip
                      label="Excellent"
                      size="small"
                      sx={{ 
                        bgcolor: 'rgba(76, 175, 80, 0.15)', 
                        color: '#4CAF50',
                        fontWeight: 500,
                        fontSize: '12px'
                      }}
                    />
                  ) : summary?.averagePosition && summary.averagePosition < 30 ? (
                    <Chip
                      label="Average"
                      size="small"
                      sx={{ 
                        bgcolor: 'rgba(255, 152, 0, 0.15)', 
                        color: '#FF9800',
                        fontWeight: 500,
                        fontSize: '12px'
                      }}
                    />
                  ) : (
                    <Chip
                      label="Needs Improvement"
                      size="small"
                      sx={{ 
                        bgcolor: 'rgba(244, 67, 54, 0.15)', 
                        color: '#F44336',
                        fontWeight: 500,
                        fontSize: '12px'
                      }}
                    />
                  )}
                </Box>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                sx={{ 
                  background: 'white',
                  borderRadius: '12px',
                  padding: '24px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                  border: '1px solid #f0f0f0',
                  height: '100%'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box 
                    sx={{ 
                      width: 40, 
                      height: 40, 
                      borderRadius: '8px',
                      bgcolor: 'rgba(76, 175, 80, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2
                    }}
                  >
                    <EmojiEventsIcon sx={{ color: '#4CAF50', fontSize: 20 }} />
                  </Box>
                  <Typography variant="subtitle2" sx={{ color: '#666', fontSize: '14px' }}>
                    Keywords in Top 10
                  </Typography>
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, color: '#333' }}>
                  {summary?.keywordsTop10 || 0}
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                  {summary
                    ? `${((summary.keywordsTop10 / summary.totalKeywords) * 100).toFixed(1)}% of total`
                    : '0% of total'}
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={summary ? (summary.keywordsTop10 / summary.totalKeywords) * 100 : 0}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: '#f0f0f0',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: '#4CAF50',
                      borderRadius: 3
                    }
                  }}
                />
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                sx={{ 
                  background: 'white',
                  borderRadius: '12px',
                  padding: '24px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                  border: '1px solid #f0f0f0',
                  height: '100%'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box 
                    sx={{ 
                      width: 40, 
                      height: 40, 
                      borderRadius: '8px',
                      bgcolor: 'rgba(33, 150, 243, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2
                    }}
                  >
                    <MouseIcon sx={{ color: '#2196F3', fontSize: 20 }} />
                  </Box>
                  <Typography variant="subtitle2" sx={{ color: '#666', fontSize: '14px' }}>
                    Total Visitors
                  </Typography>
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, color: '#333' }}>
                  {summary?.totalClicks?.toLocaleString() || 0}
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                  {summary?.totalImpressions?.toLocaleString() || 0} sessions
                </Typography>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  {summary && summary.totalImpressions
                    ? `${(summary.totalClicks / summary.totalImpressions * 100).toFixed(1)}% conversion`
                    : 'No data'}
                </Typography>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                sx={{ 
                  background: 'white',
                  borderRadius: '12px',
                  padding: '24px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                  border: '1px solid #f0f0f0',
                  height: '100%'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box 
                    sx={{ 
                      width: 40, 
                      height: 40, 
                      borderRadius: '8px',
                      bgcolor: 'rgba(255, 152, 0, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2
                    }}
                  >
                    <VisibilityIcon sx={{ color: '#FF9800', fontSize: 20 }} />
                  </Box>
                  <Typography variant="subtitle2" sx={{ color: '#666', fontSize: '14px' }}>
                    Conversion Rate
                  </Typography>
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 1, color: '#333' }}>
                  {summary?.avgCtr ? `${(summary.avgCtr * 100).toFixed(1)}%` : 'N/A'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {summary?.avgCtr && summary.avgCtr > 0.05 ? (
                    <Chip
                      label="High"
                      size="small"
                      sx={{ 
                        bgcolor: 'rgba(76, 175, 80, 0.15)', 
                        color: '#4CAF50',
                        fontWeight: 500,
                        fontSize: '12px'
                      }}
                    />
                  ) : summary?.avgCtr && summary.avgCtr > 0.02 ? (
                    <Chip
                      label="Average"
                      size="small"
                      sx={{ 
                        bgcolor: 'rgba(255, 152, 0, 0.15)', 
                        color: '#FF9800',
                        fontWeight: 500,
                        fontSize: '12px'
                      }}
                    />
                  ) : (
                    <Chip
                      label="Low"
                      size="small"
                      sx={{ 
                        bgcolor: 'rgba(244, 67, 54, 0.15)', 
                        color: '#F44336',
                        fontWeight: 500,
                        fontSize: '12px'
                      }}
                    />
                  )}
                </Box>
              </Card>
            </Grid>
          </Grid>
          
          {/* Charts Section */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={8}>
              <Paper sx={{ p: 3, bgcolor: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)', border: '1px solid #f0f0f0' }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#333', fontWeight: 600 }}>
                  Traffic Trend
                </Typography>
                <Box sx={{ height: 300 }}>
                  {(() => {
                    console.log('🎯 Chart render check:', {
                      trafficTrendLength: trafficTrend.length,
                      sampleData: trafficTrend.slice(0, 2),
                      chartDatasets: trafficChartData.datasets.map(d => ({
                        label: d.label,
                        dataLength: d.data.length,
                        sampleValues: d.data.slice(0, 3)
                      }))
                    });
                    return null;
                  })()}
                  {trafficTrend.length > 0 ? (
                    <Line
                      data={trafficChartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            position: 'top',
                            labels: {
                              color: '#666',
                              font: {
                                size: 12,
                              },
                              usePointStyle: true,
                              pointStyle: 'circle',
                            },
                          },
                        },
                        scales: {
                          x: {
                            grid: {
                              display: false,
                            },
                            ticks: {
                              color: '#666',
                              font: {
                                size: 11,
                              },
                            },
                          },
                          y: {
                            beginAtZero: true,
                            grid: {
                              color: 'rgba(0,0,0,0.05)',
                            },
                            ticks: {
                              color: '#666',
                              font: {
                                size: 11,
                              },
                              callback: function(value: any) {
                                // Format Y-axis labels to show proper numbers
                                if (value >= 1000) {
                                  return (value / 1000).toFixed(1) + 'K';
                                }
                                return value.toString();
                              }
                            },
                            title: {
                              display: true,
                              text: 'Visitors / Sessions',
                              color: '#666',
                              font: {
                                size: 12,
                              }
                            }
                          },
                        },
                        elements: {
                          line: {
                            tension: 0.4,
                          },
                        },
                      }}
                    />
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <Typography color="textSecondary">No traffic data available</Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, bgcolor: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)', border: '1px solid #f0f0f0' }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#333', fontWeight: 600 }}>
                  Ranking Distribution
                </Typography>
                <Box sx={{ height: 300 }}>
                  {summary ? (
                    <Doughnut
                      data={positionChartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            position: 'bottom',
                          },
                        },
                      }}
                    />
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <Typography color="textSecondary">No ranking data available</Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>
            
          </Grid>

          {/* Top Keywords Section */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, bgcolor: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)', border: '1px solid #f0f0f0', height: '100%' }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 3, color: '#333', fontWeight: 600 }}>
                  Top Performing Keywords
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {topKeywords.length > 0 ? topKeywords.map((item, index) => (
                    <Box 
                      key={index} 
                      sx={{ 
                        p: 2, 
                        bgcolor: '#f9f9f9',
                        borderRadius: '4px',
                        border: '1px solid #eaeaea',
                        '&:hover': {
                          bgcolor: '#f5f5f5'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: '#333', fontSize: '14px' }}>
                            {item.keyword}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5, mr: 2 }}>
                          {item.intent.map((intent: string, i: number) => {
                            const getIntentColor = (intent: string) => {
                              switch (intent) {
                                case 'IC': return { bg: 'rgba(156, 39, 176, 0.1)', color: '#9C27B0' };
                                case 'S': return { bg: 'rgba(76, 175, 80, 0.1)', color: '#4CAF50' };
                                case 'C': return { bg: 'rgba(33, 150, 243, 0.1)', color: '#2196F3' };
                                case 'V': return { bg: 'rgba(244, 67, 54, 0.1)', color: '#F44336' };
                                case 'I': return { bg: 'rgba(255, 152, 0, 0.1)', color: '#FF9800' };
                                default: return { bg: 'rgba(156, 39, 176, 0.1)', color: '#9C27B0' };
                              }
                            };
                            const colors = getIntentColor(intent);
                            return (
                              <Box 
                                key={i} 
                                sx={{ 
                                  bgcolor: colors.bg, 
                                  color: colors.color,
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  padding: '3px 6px',
                                  borderRadius: '4px',
                                  display: 'inline-block'
                                }}
                              >
                                {intent}
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ textAlign: 'left', minWidth: '60px' }}>
                          <Typography variant="caption" sx={{ color: '#555', fontSize: '12px' }}>Position</Typography>
                          <Typography variant="body1" sx={{ color: '#4CAF50', fontWeight: 600, fontSize: '14px' }}>{item.position}</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center', minWidth: '60px' }}>
                          <Typography variant="caption" sx={{ color: '#555', fontSize: '12px' }}>Clicks</Typography>
                          <Typography variant="body1" sx={{ color: '#333', fontWeight: 600, fontSize: '14px' }}>{item.clicks}</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center', minWidth: '60px' }}>
                          <Typography variant="caption" sx={{ color: '#555', fontSize: '12px' }}>CTR</Typography>
                          <Typography variant="body1" sx={{ color: '#2196F3', fontWeight: 600, fontSize: '14px' }}>{item.ctr}%</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right', minWidth: '60px' }}>
                          {item.position <= 3 ? (
                            <Box 
                              sx={{ 
                                bgcolor: 'rgba(76, 175, 80, 0.15)', 
                                color: '#4CAF50',
                                fontWeight: 500,
                                fontSize: '12px',
                                padding: '4px 10px',
                                borderRadius: '12px',
                                display: 'inline-block'
                              }}
                            >
                              +7
                            </Box>
                          ) : item.position <= 10 ? (
                            <Box 
                              sx={{ 
                                bgcolor: 'rgba(76, 175, 80, 0.15)', 
                                color: '#4CAF50',
                                fontWeight: 500,
                                fontSize: '12px',
                                padding: '4px 10px',
                                borderRadius: '12px',
                                display: 'inline-block'
                              }}
                            >
                              +4
                            </Box>
                          ) : (
                            <Box 
                              sx={{ 
                                bgcolor: 'rgba(244, 67, 54, 0.15)', 
                                color: '#F44336',
                                fontWeight: 500,
                                fontSize: '12px',
                                padding: '4px 10px',
                                borderRadius: '12px',
                                display: 'inline-block'
                              }}
                            >
                              -3
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  )) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                      <Typography color="textSecondary">No top performing keywords found</Typography>
                    </Box>
                  )}
                  {topKeywords.length > 0 && (
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                      <Button 
                        variant="outlined" 
                        size="small"
                        onClick={handleLoadMoreTop}
                        sx={{ 
                          borderRadius: '4px',
                          textTransform: 'none',
                          fontSize: '12px',
                          color: '#666',
                          borderColor: '#eaeaea',
                          '&:hover': {
                            borderColor: '#ddd',
                            bgcolor: '#f5f5f5'
                          }
                        }}
                      >
                        Load More
                      </Button>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, bgcolor: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)', border: '1px solid #f0f0f0', height: '100%' }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 3, color: '#333', fontWeight: 600 }}>
                  Keywords Needing Attention
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {underperformingKeywords.length > 0 ? underperformingKeywords.map((item, index) => (
                    <Box 
                      key={index} 
                      sx={{ 
                        p: 2, 
                        bgcolor: '#f9f9f9',
                        borderRadius: '4px',
                        border: '1px solid #eaeaea',
                        '&:hover': {
                          bgcolor: '#f5f5f5'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 600, color: '#333', fontSize: '14px' }}>
                            {item.keyword}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5, mr: 2 }}>
                          {item.intent.map((intent: string, i: number) => {
                            const getIntentColor = (intent: string) => {
                              switch (intent) {
                                case 'IC': return { bg: 'rgba(156, 39, 176, 0.1)', color: '#9C27B0' };
                                case 'S': return { bg: 'rgba(76, 175, 80, 0.1)', color: '#4CAF50' };
                                case 'C': return { bg: 'rgba(33, 150, 243, 0.1)', color: '#2196F3' };
                                case 'V': return { bg: 'rgba(244, 67, 54, 0.1)', color: '#F44336' };
                                case 'I': return { bg: 'rgba(255, 152, 0, 0.1)', color: '#FF9800' };
                                default: return { bg: 'rgba(156, 39, 176, 0.1)', color: '#9C27B0' };
                              }
                            };
                            const colors = getIntentColor(intent);
                            return (
                              <Box 
                                key={i} 
                                sx={{ 
                                  bgcolor: colors.bg, 
                                  color: colors.color,
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  padding: '3px 6px',
                                  borderRadius: '4px',
                                  display: 'inline-block'
                                }}
                              >
                                {intent}
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ textAlign: 'left', minWidth: '60px' }}>
                          <Typography variant="caption" sx={{ color: '#555', fontSize: '12px' }}>Position</Typography>
                          <Typography variant="body1" sx={{ color: '#F44336', fontWeight: 600, fontSize: '14px' }}>#{item.position}</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center', minWidth: '60px' }}>
                          <Typography variant="caption" sx={{ color: '#555', fontSize: '12px' }}>Clicks</Typography>
                          <Typography variant="body1" sx={{ color: '#333', fontWeight: 600, fontSize: '14px' }}>{item.clicks}</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'center', minWidth: '60px' }}>
                          <Typography variant="caption" sx={{ color: '#555', fontSize: '12px' }}>CTR</Typography>
                          <Typography variant="body1" sx={{ color: '#2196F3', fontWeight: 600, fontSize: '14px' }}>{item.ctr}%</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right', minWidth: '60px' }}>
                          <Box 
                            sx={{ 
                              bgcolor: 'rgba(244, 67, 54, 0.15)', 
                              color: '#F44336',
                              fontWeight: 500,
                              fontSize: '12px',
                              padding: '4px 10px',
                              borderRadius: '12px',
                              display: 'inline-block'
                            }}
                          >
                            -3
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  )) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                      <Typography color="textSecondary">No underperforming keywords found</Typography>
                    </Box>
                  )}
                  {underperformingKeywords.length > 0 && (
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                      <Button 
                        variant="outlined" 
                        size="small"
                        onClick={handleLoadMoreUnder}
                        sx={{ 
                          borderRadius: '4px',
                          textTransform: 'none',
                          fontSize: '12px',
                          color: '#666',
                          borderColor: '#eaeaea',
                          '&:hover': {
                            borderColor: '#ddd',
                            bgcolor: '#f5f5f5'
                          }
                        }}
                      >
                        Load More
                      </Button>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default DashboardPage;